import { useEffect, useState } from 'react'

/**
 * The four fields of a React Query result this cares about.
 *
 * Structural, so a caller passes the query object straight through — `useScheduleFreshness([
 * scheduleQuery, tasksQuery])` type-checks without any adapter. Taking results as arguments rather
 * than calling the queries in here keeps this a pure combinator: it needs no knowledge of the
 * `{ from, to, technicianIds }` argument shape, adds no second observer on a key someone else is
 * already watching, and is testable with object literals instead of MSW.
 */
export interface SyncSource {
  dataUpdatedAt: number
  isFetching: boolean
  isError: boolean
  isPaused: boolean
}

export interface ScheduleFreshness {
  /** Epoch ms of the **oldest** successful fetch across all sources; null until all have succeeded. */
  lastUpdatedAt: number | null
  isFetching: boolean
  isDisconnected: boolean
}

/**
 * One freshness answer for a screen fed by several queries.
 *
 * **Worst-of, on purpose.** The worker calendar draws appointments from `useSchedule` and tasks
 * from `useTechnicianTasks`. A screen whose appointments are current and whose task list is ten
 * minutes old is a stale screen — reporting the newer of the two would hide exactly the failure
 * this exists to catch, a task assigned this morning that never arrived. So `lastUpdatedAt` is the
 * minimum, and one unhealthy source is enough to set `isDisconnected`.
 *
 * **Why the latch.** Both queries use `placeholderData: (prev) => prev` and put the visible day
 * window in their key. Tapping the next-day arrow therefore produces a key whose `dataUpdatedAt` is
 * `0` while the previous day's data is still painted on screen. Reporting that directly would blank
 * the badge to "never updated" on every arrow tap. The latch keeps the last non-zero stamp, so the
 * badge describes the data the worker can actually see — which is the same commitment
 * `useBufferedCalendarData` already makes about the columns themselves.
 *
 * **Last write wins, not `Math.max`.** Tempting, and wrong here. The work-order page's equivalent
 * gets monotonicity for free because its key never changes; this key does. Tap next, then prev, and
 * React Query serves the *previously cached* entry with its *older* stamp. `Math.max` would go on
 * reporting the newer day's fetch time over the older day's data, under-reporting staleness in
 * precisely the "flip back to check yesterday's plan" case.
 */
export function useScheduleFreshness(sources: readonly SyncSource[]): ScheduleFreshness {
  const stamps = sources.map((s) => s.dataUpdatedAt)
  const [latched, setLatched] = useState<(number | null)[]>(() => stamps.map((t) => t || null))

  // Keyed on the stamps themselves: a variable-length numeric array has no honest dependency other
  // than its contents, and returning `prev` unchanged is what stops this from looping.
  const stampKey = stamps.join('|')

  useEffect(() => {
    setLatched((prev) => {
      let changed = prev.length !== stamps.length
      const next = stamps.map((t, i) => {
        const keep = prev[i] ?? null
        if (t && t !== keep) {
          changed = true
          return t
        }
        return keep
      })
      return changed ? next : prev
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stampKey])

  // The latch is a render behind on first commit, so read through to the live stamp per source
  // rather than reporting "never updated" for a frame.
  const resolved = stamps.map((t, i) => latched[i] ?? t ?? 0)
  const lastUpdatedAt =
    resolved.length > 0 && resolved.every((t) => t > 0) ? Math.min(...resolved) : null

  return {
    lastUpdatedAt,
    isFetching: sources.some((s) => s.isFetching),
    isDisconnected: sources.some((s) => s.isPaused || s.isError),
  }
}
